import React, { useMemo } from 'react';

import {
  EmailPreview,
  RequestedEmailData,
} from '@work4all/components/lib/components/entity-preview/email-preview';
import { useEntityPreviewData } from '@work4all/components/lib/components/entity-preview/hooks/use-entity-preview-data';
import { EntityPreviewContainerProps } from '@work4all/components/lib/components/entity-preview/types';

import { usePermissions } from '@work4all/data';
import { useSyncEmailStatus } from '@work4all/data/lib/hooks/use-sync-email-status';

import { EMail } from '@work4all/models/lib/Classes/EMail.entity';
import { EMailKind } from '@work4all/models/lib/Enums/EMailKind.enum';
import { Entities } from '@work4all/models/lib/Enums/Entities.enum';

import { EntityPreviewDataProviderProps } from '../../../../../../components/data-tables/entity-table/types';
import { mergeActions } from '../../../../../../components/data-tables/entity-table/utils';
import { useEmailActions } from '../../../../../mask-overlays/mask-overlay/views/email/EmailActions';

type IEmailPreviewContainerProps = EntityPreviewContainerProps & {
  entries?: { id?: string | number }[];
};

const additionalVars = { rewriteInlineAttachements: true };

export function EmailPreviewContainer(props: IEmailPreviewContainerProps) {
  return <EmailPreview {...props} entries={props.entries as EMail[]} />;
}

export const EmailPreviewDataProvider = (
  props: EntityPreviewDataProviderProps & {
    children: (props: EntityPreviewDataProviderProps) => React.ReactNode;
  }
) => {
  const { children, ...dataProps } = props;

  const { entries, ...others } = dataProps;
  const ids = entries.map((e) => e.id);

  // Extract ids from entries if ids is not provided
  const emailIds =
    ids ||
    (entries
      ?.map((entry) => entry.id)
      .filter((id) => id !== undefined) as number[]) ||
    [];

  const emails = useEntityPreviewData<EMail>(
    emailIds as number[],
    Entities.eMail,
    RequestedEmailData,
    additionalVars
  );

  const email = emails?.[0];

  useSyncEmailStatus({
    disabled: email == null,
    shouldSync({ id }) {
      return email?.id === id;
    },
  });

  const { canAdd } = usePermissions();

  const { emailActionDefinitions } = useEmailActions({
    hasRight: canAdd({ entity: Entities.eMail }),
    outbound: email?.kind === EMailKind.AUSGEHEND ? true : false,
  });

  const custom = useMemo(
    () =>
      mergeActions(props.custom, {
        left: emailActionDefinitions,
      }),
    [props.custom, emailActionDefinitions]
  );

  if (!emails) {
    return null;
  }

  return (
    <>
      {children({
        ...others,
        entries: emails,
        custom,
      })}
    </>
  );
};
